import React from "react";
import { CSS_VARS } from "../../lib/colorSystem";
import { formatCompact } from "@/utils/formatters";

// =============================================================================
// GOAL PROGRESS BADGE
// =============================================================================
// PURPOSE: Shows progress toward a goal (e.g., "54% of goal") instead of
// comparing to a previous period. Better for expectation management.
//
// DESIGN:
// - Shows percentage of goal achieved
// - Color-coded: green if on/ahead of pace, amber if slightly behind, red if far behind
// - Optional "pace" indicator based on days elapsed
// =============================================================================

interface GoalProgressBadgeProps {
  current: number;
  goal: number;
  /** Days elapsed in the period */
  daysElapsed?: number;
  /** Total days in the period */
  totalDays?: number;
  /** Format: "percent" (54% of goal) or "ratio" (54K / 150K) */
  format?: "percent" | "ratio";
}

const GoalProgressBadge: React.FC<GoalProgressBadgeProps> = ({
  current,
  goal,
  daysElapsed,
  totalDays,
  format = "percent",
}) => {
  if (!goal || goal <= 0) return null;

  const progressPercent = (current / goal) * 100;

  // Calculate expected progress if we have pace info
  let expectedPercent = 100; // Default to expecting 100% at end
  if (daysElapsed !== undefined && totalDays && totalDays > 0) {
    expectedPercent = (daysElapsed / totalDays) * 100;
  }

  // Determine status: on pace, ahead, or behind
  const paceRatio = progressPercent / expectedPercent;
  const isAhead = paceRatio >= 1;
  const isClose = paceRatio >= 0.85;

  // Color based on pace
  const color = isAhead ? CSS_VARS.green : isClose ? CSS_VARS.amber : CSS_VARS.red;
  const bgColor = isAhead ? CSS_VARS.greenBg : isClose ? CSS_VARS.amberBg : CSS_VARS.redBg;

  const displayValue =
    format === "ratio"
      ? `${formatCompact(current)} / ${formatCompact(goal)}`
      : `${progressPercent.toFixed(0)}% of goal`;

  return (
    <span
      className="inline-flex items-center token-11 font-semibold px-2 py-0.5 rounded-md"
      style={{ color, backgroundColor: bgColor }}
    >
      {displayValue}
    </span>
  );
};

export default GoalProgressBadge;
